import React from "react";
import { clsx } from "clsx";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";

import {
    type MediaConfig,
    Media,
    MediaFormat,
} from "~components/shared/media/";
import { StoryBlock } from "~components/shared/story-block/StoryBlock";
import { VariantBaseWithHeader } from "~components/shared/variant-base/VariantBase";
import { TranslatedMessage } from "~i18n/translate";
import type { MagazineModule } from "~models/transforms/magazineModules";
import {
    type CuratedItem,
    type VariationComponent,
    CuratedItemVariation,
    ZoneColumnCombo,
} from "~types/types";
import { integrityError } from "~utils/moduleIntegrity";

import integrityCheck from "./LineupContentMagazine.integrity";
import { PREVIEW_NOTES } from "./previewNotes";
import styles from "./LineupContentMagazine.module.css";

export const MEDIA_PRIORITY: MediaFormat[] = [
    MediaFormat.vodVideo,
    MediaFormat.loopingVideo,
    MediaFormat.defaultImage,
];
const PRIMARY_STORY_MEDIA_CONFIG: MediaConfig = {
    aspectRatio: 3 / 2,
    className: styles.primaryStoryMedia,
    desktopMaxWidth: 353,
};
const SECONDARY_STORY_MEDIA_CONFIG: MediaConfig = {
    aspectRatio: 3 / 2,
    className: styles.secondaryStoryMedia,
    desktopMaxWidth: 194,
    showCaption: false,
};
const SUPPORTED_REGIONS: ZoneColumnCombo[] = [
    ZoneColumnCombo.fullWidthContentWell,
];
const MAX_RELATED_IN_COVER_BOX = 3;

const PrimaryStory = ({ story }: { story: CuratedItem }) => {
    return (
        <div className={styles.primaryStory}>
            <Media
                source={story}
                priority={MEDIA_PRIORITY}
                config={PRIMARY_STORY_MEDIA_CONFIG}
            />
            <div className={styles.primaryStoryBlock}>
                <StoryBlock
                    storyIndex={1}
                    story={story}
                    withDek={Boolean(story.summary)}
                    forceShowByline={
                        story.variation !==
                        CuratedItemVariation.FeaturedWithoutByline
                    }
                    isBylineEligible
                    isFullZone
                />
            </div>
        </div>
    );
};

const CoverStory = ({ magazineModule }: { magazineModule: MagazineModule }) => {
    if (
        !magazineModule.magazineIssueUrl ||
        !magazineModule.magazineCoverStory
    ) {
        return null;
    }

    const { origHeight, origWidth } = magazineModule.magazineCoverImage || {};
    const aspectRatio =
        origWidth && origHeight ? origWidth / origHeight : 3 / 4;

    const coverStoryMediaConfig: MediaConfig = {
        aspectRatio,
        altText: magazineModule.magazineIssueDate,
        className: styles.coverStoryMedia,
        desktopMaxWidth: 135,
        mobileMaxWidth: 135,
        showCaption: false,
        useImageLink: false,
    };

    return (
        <div className={styles.coverStory}>
            <div className={styles.coverStoryContainer}>
                <a href={magazineModule.magazineIssueUrl}>
                    <Media
                        source={magazineModule.magazineCoverStory}
                        priority={[MediaFormat.magazineCoverImage]}
                        config={coverStoryMediaConfig}
                    />
                </a>
                <div className={styles.coverStoryRelatedItems}>
                    <h3 className={typographyStyles.articleDek}>
                        <TranslatedMessage
                            id="moreFromThisIssue"
                            defaultMessage="More from this issue:"
                        />
                    </h3>

                    {(magazineModule.items || [])
                        .slice(0, MAX_RELATED_IN_COVER_BOX)
                        .map((story, index) => {
                            return (
                                <a
                                    data-link-index={2}
                                    data-link-related-type="Related"
                                    data-link-related-index={index + 1}
                                    href={story.url}
                                    key={story.url}
                                    className={clsx(
                                        styles.relatedItem,
                                        typographyStyles.articleLabelMd
                                    )}
                                >
                                    {story.headline}
                                </a>
                            );
                        })}
                </div>
            </div>
        </div>
    );
};

const SecondaryStory = ({ story }: { story: CuratedItem }) => {
    if (!story) return null;

    return (
        <div className={styles.secondaryStory}>
            <Media
                source={story}
                priority={MEDIA_PRIORITY}
                config={SECONDARY_STORY_MEDIA_CONFIG}
            />
            <div className={styles.secondaryStoryBlock}>
                <StoryBlock
                    storyIndex={3}
                    story={story}
                    withDek={Boolean(story.summary)}
                    isFullZone
                />
            </div>
        </div>
    );
};

const TertiaryStories = ({ stories }: { stories: CuratedItem[] }) => {
    return (
        <div className={styles.tertiaryStories} data-testid="tertiary-stories">
            {stories?.map((story, index) => (
                <StoryBlock
                    storyIndex={3 + index}
                    story={story}
                    isSecondaryStory
                    isExtraSmallHeadline
                    withDek={Boolean(story.summary)}
                    className={styles.tertiaryStory}
                    key={story.url}
                />
            ))}
        </div>
    );
};

export default function LineupContentMagazine(props: VariationComponent) {
    const { items, magazineModules } = props;

    const integrity = integrityCheck(props);

    if (integrity.length) return integrityError(integrity);

    const magazineModule = magazineModules && magazineModules[0];

    const [primaryStory, secondaryStory, ...otherItems]: CuratedItem[] = items;
    const tertiaryStories = otherItems.slice(0, 2);

    return (
        <VariantBaseWithHeader
            source={props}
            className={styles.LineupContentMagazine}
            supportedRegions={SUPPORTED_REGIONS}
            previewNotes={PREVIEW_NOTES}
        >
            <div className={styles.stories}>
                <PrimaryStory story={primaryStory} />
                {magazineModule?.magazineCoverImage && (
                    <CoverStory magazineModule={magazineModule} />
                )}
                <div className={styles.borderDiv} />
                <SecondaryStory story={secondaryStory} />
                <div className={styles.secondBorderDiv} />
                <TertiaryStories stories={tertiaryStories} />
            </div>
        </VariantBaseWithHeader>
    );
}
